import { useCallback } from 'react';

import { Avatar, Card, Text, useBreakpoint, VSpacing } from '@hh.ru/magritte-ui';
import Conversion from 'bloko/blocks/conversion';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import { updateUrl } from 'Modules/url';
import paths from 'src/app/routePaths';
import translation from 'src/components/translation';
import { useCdnHost } from 'src/hooks/useCdnImageHost';
import { useSelector } from 'src/hooks/useSelector';
import { EmployerHint, Logos } from 'src/models/vacancyWizard';

import CollapsedTextWithHint from 'src/components/VacancySearchContent/components/VacancyWizard/Magritte/CollapsedTextWithHint';

import styles from './styles.less';

const employerWizardLogo = (logos: Logos, cdnImageHost: string): string | null => {
    const logo = logos?.logo.find((logo) => logo.type === 'vacancyPage');
    return logo ? `${cdnImageHost}${logo.url}` : null;
};

const TrlKeys = {
    vacancy: {
        one: 'employer.page.vacancy.one',
        some: 'employer.page.vacancy.some',
        many: 'employer.page.vacancy.many',
    },
};

const VacancySearchWizardGalleryCard: TranslatedComponent<EmployerHint> = ({
    id,
    name,
    logos,
    vacanciesOpen,
    trls,
}) => {
    const searchWizardBase = useSelector((state) => state.vacancySearchResult.queries.searchWizardBase) || '';

    const cdnImageHost = useCdnHost();
    const logoUrl = employerWizardLogo(logos, cdnImageHost);

    const handleClick = useCallback(
        () =>
            window.open(
                updateUrl(`${paths.vacancySearch}?${searchWizardBase}`, { [`employer_id`]: id }),
                '_self',
                'noopener'
            ),
        [id, searchWizardBase]
    );

    const { isGtM } = useBreakpoint();

    return (
        <Card
            borderWidth="default"
            borderRadius={isGtM ? 24 : 16}
            padding={isGtM ? 16 : 12}
            onClick={handleClick}
            data-qa="wizard-item"
        >
            <div className={styles.content}>
                <div className={styles.logoWrapper}>
                    {logoUrl ? (
                        <img
                            className={styles.logo}
                            src={logoUrl}
                            loading="lazy"
                            alt={name}
                            data-qa="wizard-company-logo"
                        />
                    ) : (
                        <Avatar mode="placeholder" placeholder="city" size={40} aria-label={name} shapeCircle />
                    )}
                </div>
                <VSpacing default={12} />
                <CollapsedTextWithHint>{name}</CollapsedTextWithHint>
                <Text typography="label-4-regular" style="secondary" data-qa="wizard-company-active-vacancies">
                    <Conversion
                        value={vacanciesOpen}
                        one={trls[TrlKeys.vacancy.one]}
                        some={trls[TrlKeys.vacancy.some]}
                        many={trls[TrlKeys.vacancy.many]}
                    />
                </Text>
            </div>
        </Card>
    );
};

export default translation(VacancySearchWizardGalleryCard);
